/**
 * 路由级 lazy 包装 · 修复「发版后旧会话动态 import 失败」（2026-11-05 生产报障）
 *
 * 背景：Cloudflare Pages 主域只服务当前部署的 assets；重新发版后，旧页面会话
 * （SPA 长开会话或旧 HTML 缓存）懒加载路由时引用的旧 chunk 已被清理，`_redirects`
 * 的 SPA 回退把该 URL 顶回 text/html，浏览器 import() 拿到 HTML 报
 * 「Failed to fetch dynamically imported module」（2026-11-04 17:35 发版后实测复现）。
 *
 * 策略：import 失败时若本会话尚未自动 reload 过（或距上次已超 10s），写
 * sessionStorage 时间戳标记后整页 reload 一次拉取新 HTML；10s 内连续失败
 * （真网络故障/新部署同样损坏）则原样抛出交由 ErrorBoundary 兜底，防死循环。
 */
import { lazy, type ComponentType } from 'react';

const RELOAD_FLAG = 'lazyRouteReloadAt';
/** 两次自动 reload 的最小间隔：覆盖单次发版窗口，同时拦截真网络故障下的死循环 */
const RELOAD_MIN_INTERVAL_MS = 10_000;

type ChunkStorage = Pick<Storage, 'getItem' | 'setItem'> | null;

/** chunk 加载失败后是否应整页 reload（供单元测试的纯判定） */
export function shouldReloadAfterChunkFailure(
  storage: ChunkStorage,
  now: number,
): boolean {
  if (!storage) {
    // 隐私模式等 storage 不可用：仍 reload 一次（标记无法跨 reload，接受罕见双重 reload）
    return true;
  }
  let last: number = NaN;
  try {
    last = Number(storage.getItem(RELOAD_FLAG));
  } catch {
    return true;
  }
  if (!Number.isFinite(last) || last <= 0) {
    return true;
  }
  return now - last > RELOAD_MIN_INTERVAL_MS;
}

/** 记录本次自动 reload 时间戳（写失败仅放弃标记，不阻断恢复路径） */
export function markChunkReload(storage: ChunkStorage, now: number): void {
  try {
    storage?.setItem(RELOAD_FLAG, String(now));
  } catch {
    // 配额/隐私模式写失败：宁可罕见双重 reload，不抛出
  }
}

/**
 * 与 React.lazy 同形：factory 失败时按 {@link shouldReloadAfterChunkFailure}
 * 决定整页 reload（挂起渲染）或原样抛出。
 */
export function lazyRoute(factory: () => Promise<{ default: ComponentType }>) {
  const storage: ChunkStorage =
    typeof sessionStorage !== 'undefined' ? sessionStorage : null;
  return lazy(() =>
    factory().catch((err: unknown) => {
      const now = Date.now();
      if (shouldReloadAfterChunkFailure(storage, now)) {
        markChunkReload(storage, now);
        window.location.reload();
        // reload 已发起：永不 resolve 挂起本次渲染，避免 React 用旧树继续渲染
        return new Promise<{ default: ComponentType }>(() => {});
      }
      throw err;
    }),
  );
}
